import { motion, useInView, AnimatePresence, useReducedMotion } from 'motion/react';
import { useRef, useState } from 'react';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { isFinnishPage } from '../../lib/blogPaths';

const slideVariants = {
  enterNext: { opacity: 0, x: 24 },
  enterPrev: { opacity: 0, x: -24 },
  center: { opacity: 1, x: 0 },
  exitNext: { opacity: 0, x: -24 },
  exitPrev: { opacity: 0, x: 24 },
};

const DEFAULT_TESTIMONIALS = [
  {
    quote:
      'At Smartum, we are very satisfied with our cooperation and the expertise that Identio has used to complement our development team. In particular, we value the quality of the code, their ability to cooperate and teamwork skills, high-quality and professional working methods, and initiative and commitment to achieving the goals set for the team.',
    author: 'Anne Silván',
    title: 'Product Manager',
    company: 'Smartum',
    brandColor: '#0f172a',
  },
  {
    quote:
      'Identio has helped us a lot in developing web user interfaces. Even when the projects and commissions changed, they were able to meet the defined needs with their great competences and by adapting to different situations. Our cooperation was smooth, and the results were professional. We recommend them!',
    author: 'Ville Nuutinen',
    title: 'Team Lead',
    company: 'Kiho',
    brandColor: '#0f172a',
  },
  {
    quote:
      'Identio has helped Vaadin in product development of user interface technologies. The flexibility and web technology skills of their team have made for an especially flexible cooperation.',
    author: 'Joonas Lehtinen',
    title: 'CEO',
    company: 'Vaadin',
    brandColor: '#0f172a',
  },
];

const DEFAULT_TESTIMONIALS_FI = [
  {
    quote:
      'Olemme Smartumissa erittäin tyytyväisiä yhteistyöhömme ja siihen osaamiseen, jolla Identio on täydentänyt kehitystiimiämme. Arvostamme erityisesti koodin laatua, yhteistyökykyä ja tiimityötaitoja, laadukkaita ja ammattimaisia työskentelymetodeja sekä oma-aloitteisuutta ja sitoutumista tiimille annettujen tavoitteiden saavuttamiseksi.',
    author: 'Anne Silván',
    title: 'Tuotejohtaja',
    company: 'Smartum',
    brandColor: '#0f172a',
  },
  {
    quote:
      'Identiosta oli suuri apu web-käyttöliittymien kehityksessä. Vaikka projektit ja toimeksiannot muuttuivat, määritetyt tarpeet pystyttiin toteuttamaan hyvällä osaamisella ja tilanteiden omaksumisella. Yhteistyö oli sujuvaa ja lopputulokset ammattimaisia. Kiholaiset suosittelee!',
    author: 'Ville Nuutinen',
    title: 'Tiimin johtaja',
    company: 'Kiho',
    brandColor: '#0f172a',
  },
  {
    quote:
      'Identio on auttanut Vaadinta käyttöliittymäteknologioiden tuotekehityksessä. Tiimin joustavuus ja web-teknologioiden osaaminen on tehnyt yhteistyöstä erityisen joustavaa.',
    author: 'Joonas Lehtinen',
    title: 'toimitusjohtaja',
    company: 'Vaadin',
    brandColor: '#0f172a',
  },
];

export function TestimonialsSection({
  title,
  subtitle,
  testimonials: testimonialsProp,
}) {
  const router = useRouter();
  const isFinnish = isFinnishPage(router?.pathname);
  const ref = useRef(null);
  const reducedMotion = useReducedMotion();
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const [current, setCurrent] = useState(0);
  const slideDuration = reducedMotion ? 0 : 0.3;
  const [direction, setDirection] = useState(0); // 1 = next, -1 = prev
  const defaultTestimonials = isFinnish ? DEFAULT_TESTIMONIALS_FI : DEFAULT_TESTIMONIALS;
  const defaultTitle = isFinnish ? 'Mitä asiakkaamme sanovat' : 'What Our Clients Say';
  const defaultSubtitle = isFinnish ? 'Kuule arvostetuilta yhteistyökumppaneiltamme' : 'Hear from our valued partners';
  const testimonials =
    Array.isArray(testimonialsProp) && testimonialsProp.length > 0
      ? testimonialsProp.map((t) => ({ ...t, brandColor: t.brandColor ?? '#0f172a' }))
      : defaultTestimonials;
  const displayTitle = title ?? defaultTitle;
  const displaySubtitle = subtitle ?? defaultSubtitle;

  const next = () => {
    setDirection(1);
    setCurrent((c) => (c + 1) % testimonials.length);
  };
  const prev = () => {
    setDirection(-1);
    setCurrent((c) => (c - 1 + testimonials.length) % testimonials.length);
  };
  const t = testimonials[current];

  return (
    <section ref={ref} className="py-32 bg-white">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 min-w-0">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-20"
        >
          <h2 className="heading-2 mb-4 text-gray-900 text-center">
            {displayTitle}
          </h2>
          {displaySubtitle && <p className="text-xl text-gray-500">{displaySubtitle}</p>}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="relative"
        >
          <div className="bg-white rounded-2xl p-12 shadow-xl border border-gray-200 relative overflow-hidden">
            <div className="relative z-10 min-h-[200px]">
              <AnimatePresence mode="wait" initial={false}>
                <motion.div
                  key={current}
                  initial={direction === 1 ? slideVariants.enterNext : slideVariants.enterPrev}
                  animate={slideVariants.center}
                  exit={direction === 1 ? slideVariants.exitNext : slideVariants.exitPrev}
                  transition={{ duration: slideDuration, ease: [0.32, 0.72, 0, 1] }}
                >
                  <p className="text-lg md:text-xl text-[#1a1a1a] leading-relaxed mb-8 italic">
                    &ldquo;{t.quote}&rdquo;
                  </p>
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-full flex items-center justify-center bg-[#0f172a]">
                      <span className="text-white font-bold text-xl">{t.author[0]}</span>
                    </div>
                    <div className="leading-tight">
                      <p className="font-bold text-[#1a1a1a] mb-0.5">{t.author}</p>
                      <p className="text-[#6b7280] text-sm mt-0">
                        {t.title}, {t.company}
                      </p>
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>

          <div className="flex justify-center items-center gap-4 mt-8">
            <button
              type="button"
              onClick={prev}
              className="w-12 h-12 rounded-full bg-white border border-gray-200 flex items-center justify-center hover:bg-gray-50 hover:border-[#0f172a] transition-all"
              aria-label={isFinnish ? 'Edellinen lainaus' : 'Previous testimonial'}
            >
              <ChevronLeft className="w-5 h-5 text-[#6b7280]" />
            </button>
            <div className="flex gap-2">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => {
                    setDirection(index > current ? 1 : -1);
                    setCurrent(index);
                  }}
                  className={`w-2 h-2 rounded-full transition-all ${
                    index === current ? 'bg-[#0f172a] w-8' : 'bg-gray-300'
                  }`}
                  aria-label={isFinnish ? `Siirry lainaukseen ${index + 1}` : `Go to testimonial ${index + 1}`}
                />
              ))}
            </div>
            <button
              type="button"
              onClick={next}
              className="w-12 h-12 rounded-full bg-white border border-gray-200 flex items-center justify-center hover:bg-gray-50 hover:border-[#0f172a] transition-all"
              aria-label={isFinnish ? 'Seuraava lainaus' : 'Next testimonial'}
            >
              <ChevronRight className="w-5 h-5 text-[#6b7280]" />
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

const testimonialShape = PropTypes.shape({
  quote: PropTypes.string.isRequired,
  author: PropTypes.string.isRequired,
  title: PropTypes.string,
  company: PropTypes.string,
  brandColor: PropTypes.string,
});

TestimonialsSection.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  testimonials: PropTypes.arrayOf(testimonialShape),
};
